import { Controller } from "@hotwired/stimulus";
import { serverTextValue } from "./server_text_value";

type HtmxApi = {
    ajax?: (
        method: "GET",
        path: string,
        context?: {
            source?: Element;
            target?: Element;
            swap?: string;
        }
    ) => unknown;
};

declare global {
    interface Window {
        htmx?: HtmxApi;
    }
}

const EMAIL_CONTENT_LOAD_CONCURRENCY = 10;
const EMAIL_CONTENT_PRELOAD_LIMIT = 10;
const EMAIL_CONTENT_LOAD_TIMEOUT_MS = 30000;
export default class extends Controller<HTMLElement> {
    static targets = ["content"];
    static values = {
        defaultAttachmentName: String,
        liveEmailOnDemandEnabled: Boolean,
        summaryId: String,
    };

    declare readonly contentTargets: HTMLElement[];
    declare readonly defaultAttachmentNameValue: string;
    declare readonly liveEmailOnDemandEnabledValue: boolean;
    declare readonly summaryIdValue: string;
    declare readonly hasDefaultAttachmentNameValue: boolean;
    declare readonly hasSummaryIdValue: boolean;

    private summaryElement: HTMLElement | null = null;
    private pendingOpenLoad: number | null = null;
    private loadSequenceId = 0;
    private intersectionObserver: IntersectionObserver | null = null;
    private readonly queuedContents = new Set<HTMLElement>();
    private readonly forcedQueuedContents = new Set<HTMLElement>();
    private readonly loadingContents = new Set<HTMLElement>();
    private activeLoadCount = 0;
    private liveEmailOnDemandEnabled = false;

    private readonly handleHover = (): void => {
        if (!this.liveEmailOnDemandEnabled) {
            this.armContents();
        }
    };

    private readonly handleClick = (): void => {
        if (!this.liveEmailOnDemandEnabled) {
            this.armContents();
        }
        this.scheduleLoadIfOpen();
    };

    private readonly handleAttachmentPreviewClick = (event: MouseEvent): void => {
        const trigger = this.attachmentPreviewTriggerFromEvent(event);

        if (!trigger) {
            return;
        }

        this.dispatchAttachmentPreview(event, trigger);
    };

    private readonly handleAttachmentPreviewKeydown = (event: KeyboardEvent): void => {
        if (event.key !== "Enter" && event.key !== " ") {
            return;
        }

        const trigger = this.attachmentPreviewTriggerFromEvent(event);

        if (!trigger) {
            return;
        }

        this.dispatchAttachmentPreview(event, trigger);
    };

    connect(): void {
        this.summaryElement = this.findSummaryElement();

        if (!this.summaryElement) {
            return;
        }

        this.element.dataset.ticketTriggerEmailPrefetchReady = "true";
        this.liveEmailOnDemandEnabled = this.liveEmailOnDemandEnabledValue;
        this.element.addEventListener("mouseenter", this.handleHover, { passive: true });
        this.element.addEventListener("touchstart", this.handleHover, { passive: true });
        window.addEventListener("click", this.handleAttachmentPreviewClick, true);
        window.addEventListener("keydown", this.handleAttachmentPreviewKeydown, true);
        this.summaryElement.addEventListener("mouseenter", this.handleHover, { passive: true });
        this.summaryElement.addEventListener("touchstart", this.handleHover, { passive: true });
        this.summaryElement.addEventListener("click", this.handleClick, true);

        if (this.liveEmailOnDemandEnabled) {
            this.observeContents();
            if (this.isDetailsOpen()) {
                this.scheduleLoadIfOpen();
            }
        } else {
            this.armContents();
            void this.loadContentsWithLimit(
                this.nextLoadSequenceId(),
                this.contentTargets,
                true
            );
        }
    }

    contentTargetConnected(content: HTMLElement): void {
        if (!this.liveEmailOnDemandEnabled) {
            return;
        }

        this.intersectionObserver?.observe(content);
        this.scheduleLoadIfOpen();
    }

    loadEmailIfOpen(event: Event): void {
        const details = event.currentTarget;

        if (!(details instanceof HTMLDetailsElement) || !details.open || !this.isDetailsOpen()) {
            return;
        }

        if (this.liveEmailOnDemandEnabled) {
            this.enqueueContents(this.contentTargetsWithin(details));
        } else {
            this.armContents(details);
            void this.loadContentsWithLimit(
                this.nextLoadSequenceId(),
                this.contentTargetsWithin(details),
                true
            );
        }
    }

    syncQuoteState(event: Event): void {
        const details = event.currentTarget;
        if (details instanceof HTMLDetailsElement) {
            details.querySelector(":scope > summary")?.setAttribute("aria-expanded", String(details.open));
        }
    }

    submitFormOnPointerDown(event: PointerEvent): void {
        if (event.button !== 0) {
            return;
        }

        const target = event.currentTarget;

        if (!(target instanceof HTMLElement)) {
            return;
        }

        const form = target.closest("form");

        if (!(form instanceof HTMLFormElement)) {
            return;
        }

        event.preventDefault();
        form.requestSubmit();
    }

    disconnect(): void {
        if (this.pendingOpenLoad !== null) {
            window.clearTimeout(this.pendingOpenLoad);
            this.pendingOpenLoad = null;
        }
        this.loadSequenceId += 1;
        this.intersectionObserver?.disconnect();
        this.intersectionObserver = null;
        this.queuedContents.clear();
        this.forcedQueuedContents.clear();

        if (!this.summaryElement) {
            return;
        }

        this.element.removeEventListener("mouseenter", this.handleHover);
        this.element.removeEventListener("touchstart", this.handleHover);
        window.removeEventListener("click", this.handleAttachmentPreviewClick, true);
        window.removeEventListener("keydown", this.handleAttachmentPreviewKeydown, true);
        this.summaryElement.removeEventListener("mouseenter", this.handleHover);
        this.summaryElement.removeEventListener("touchstart", this.handleHover);
        this.summaryElement.removeEventListener("click", this.handleClick, true);
        delete this.element.dataset.ticketTriggerEmailPrefetchReady;
        this.summaryElement = null;
    }

    private findSummaryElement(): HTMLElement | null {
        if (this.hasSummaryIdValue && this.summaryIdValue) {
            const summary = document.getElementById(this.summaryIdValue);
            return summary instanceof HTMLElement && this.element.contains(summary) ? summary : null;
        }

        const summary = this.element.querySelector("summary");
        return summary instanceof HTMLElement ? summary : null;
    }

    private attachmentPreviewTriggerFromEvent(event: Event): HTMLElement | null {
        const target = event.target;

        if (!(target instanceof Element)) {
            return null;
        }

        const trigger = target.closest("[data-ticket-trigger-email-attachment-preview='true']");
        return trigger instanceof HTMLElement && this.element.contains(trigger) ? trigger : null;
    }

    private dispatchAttachmentPreview(event: Event, trigger: HTMLElement): void {
        event.preventDefault();
        event.stopPropagation();

        this.element.dispatchEvent(
            new CustomEvent("ticket-attachment-preview:open", {
                bubbles: true,
                detail: {
                    url: trigger.dataset.attachmentUrl || "",
                    mimeType: trigger.dataset.attachmentMimeType || "",
                    displayName: trigger.dataset.attachmentDisplayName || this.defaultAttachmentName(),
                },
            })
        );
    }

    private defaultAttachmentName(): string {
        return serverTextValue({
            controller: this.identifier,
            fallback: "attachment",
            hasValue: this.hasDefaultAttachmentNameValue,
            name: "defaultAttachmentName",
            value: this.defaultAttachmentNameValue,
        });
    }

    private armContents(root: Node = this.element): void {
        const armElement = window.htmxInstantClick?.armElement;

        if (!armElement) {
            return;
        }

        this.contentTargetsWithin(root).forEach((content) => {
            armElement(content);
        });
    }

    private scheduleLoadIfOpen(): void {
        if (this.pendingOpenLoad !== null) {
            window.clearTimeout(this.pendingOpenLoad);
        }

        this.pendingOpenLoad = window.setTimeout(() => {
            this.pendingOpenLoad = null;

            if (!this.isDetailsOpen()) {
                return;
            }

            if (this.liveEmailOnDemandEnabled) {
                this.enqueueContents(
                    this.contentTargets.slice(0, EMAIL_CONTENT_PRELOAD_LIMIT),
                    true
                );
            } else {
                void this.loadContentsWithLimit(
                    this.nextLoadSequenceId(),
                    this.contentTargets,
                    true
                );
            }
        }, 0);
    }

    private observeContents(): void {
        if (!("IntersectionObserver" in window)) {
            this.enqueueContents(this.contentTargets.filter((content) => this.isContentVisible(content)));
            return;
        }

        this.intersectionObserver = new IntersectionObserver(
            (entries) => {
                const visibleContents = entries
                    .filter((entry) => entry.isIntersecting)
                    .map((entry) => entry.target)
                    .filter((target): target is HTMLElement => target instanceof HTMLElement);
                this.enqueueContents(visibleContents);
            },
            {
                root: this.detailScrollRoot(),
                rootMargin: "120px 0px",
                threshold: 0.01,
            }
        );

        this.contentTargets.forEach((content) => this.intersectionObserver?.observe(content));
    }

    private detailScrollRoot(): HTMLElement | null {
        const root = this.element.closest("#tickets-detail-action-scroll");
        return root instanceof HTMLElement ? root : null;
    }

    private isContentVisible(content: HTMLElement): boolean {
        if (!this.canLoadContent(content)) {
            return false;
        }

        const contentRect = content.getBoundingClientRect();
        const rootRect = this.detailScrollRoot()?.getBoundingClientRect() ?? {
            top: 0,
            right: window.innerWidth,
            bottom: window.innerHeight,
            left: 0,
        };
        return (
            contentRect.bottom >= rootRect.top - 120 &&
            contentRect.top <= rootRect.bottom + 120 &&
            contentRect.right >= rootRect.left &&
            contentRect.left <= rootRect.right
        );
    }

    private enqueueContents(contents: HTMLElement[], force = false): void {
        contents.forEach((content) => {
            if (
                (force || this.canLoadContent(content)) &&
                !this.isContentLoaded(content) &&
                !this.loadingContents.has(content) &&
                !this.queuedContents.has(content)
            ) {
                this.queuedContents.add(content);
                if (force) {
                    this.forcedQueuedContents.add(content);
                }
            }
        });
        this.pumpLoadQueue();
    }

    private pumpLoadQueue(): void {
        while (
            this.activeLoadCount < EMAIL_CONTENT_LOAD_CONCURRENCY &&
            this.queuedContents.size > 0
        ) {
            const content = this.queuedContents.values().next().value as HTMLElement | undefined;
            if (!content) {
                return;
            }

            this.queuedContents.delete(content);
            const force = this.forcedQueuedContents.delete(content);
            if (
                (!force && !this.canLoadContent(content)) ||
                this.isContentLoaded(content) ||
                this.loadingContents.has(content)
            ) {
                continue;
            }

            const sequenceId = this.loadSequenceId;
            this.activeLoadCount += 1;
            this.loadingContents.add(content);
            void this.loadContentAndWait(content).finally(() => {
                this.activeLoadCount -= 1;
                this.loadingContents.delete(content);
                if (sequenceId === this.loadSequenceId) {
                    this.pumpLoadQueue();
                }
            });
        }
    }

    private isDetailsOpen(): boolean {
        const details = this.findSummaryElement()?.closest("details");

        if (details instanceof HTMLDetailsElement) {
            return details.open;
        }

        return true;
    }

    private isContentLoaded(content: HTMLElement): boolean {
        return Boolean(
            content.querySelector('[data-testid="ticket-trigger-email-live-content-loaded"]') ||
                content.querySelector('[data-testid="ticket-trigger-email-live-content-error"]')
        );
    }

    private canLoadContent(content: HTMLElement): boolean {
        const collapsibleEmail = content.closest("[data-ticket-trigger-email-collapsible='true']");

        if (collapsibleEmail instanceof HTMLDetailsElement && !collapsibleEmail.open) {
            return false;
        }

        return this.isDetailsOpen();
    }

    private loadContent(content: HTMLElement): boolean {
        const requestPath = content.getAttribute("hx-get") || content.getAttribute("data-hx-get");
        const ajax = window.htmx?.ajax;

        if (!requestPath || !ajax) {
            return false;
        }

        void Promise.resolve(ajax("GET", requestPath, {
            source: content,
            target: content,
            swap: "innerHTML",
        })).catch(error => console.warn("[ticket-trigger-email-prefetch] request failed", error));
        return true;
    }

    private async loadContentsWithLimit(
        sequenceId: number,
        contents: HTMLElement[],
        force = false
    ): Promise<void> {
        const pendingContents = [...contents];
        let nextIndex = 0;

        const worker = async (): Promise<void> => {
            while (sequenceId === this.loadSequenceId) {
                const content = pendingContents[nextIndex];
                nextIndex += 1;

                if (!content) {
                    return;
                }

                if (!force && !this.canLoadContent(content)) {
                    continue;
                }

                if (this.isContentLoaded(content)) {
                    continue;
                }

                await this.loadContentAndWait(content);
            }
        };

        const workerCount = Math.min(EMAIL_CONTENT_LOAD_CONCURRENCY, pendingContents.length);
        await Promise.all(Array.from({ length: workerCount }, () => worker()));
    }

    private loadContentAndWait(content: HTMLElement): Promise<void> {
        return new Promise((resolve) => {
            let timeout: number | null = null;
            const eventNames = ["htmx:afterSettle", "htmx:responseError", "htmx:sendError", "htmx:timeout"];

            const cleanup = (): void => {
                if (timeout !== null) {
                    window.clearTimeout(timeout);
                    timeout = null;
                }

                eventNames.forEach((eventName) => {
                    content.removeEventListener(eventName, finish);
                });
            };

            const finish = (): void => {
                cleanup();
                resolve();
            };

            eventNames.forEach((eventName) => {
                content.addEventListener(eventName, finish, { once: true });
            });

            timeout = window.setTimeout(finish, EMAIL_CONTENT_LOAD_TIMEOUT_MS);

            if (!this.loadContent(content)) {
                finish();
            }
        });
    }

    private contentTargetsWithin(root: Node): HTMLElement[] {
        return this.contentTargets.filter((content) => root === this.element || root.contains(content));
    }

    private nextLoadSequenceId(): number {
        const sequenceId = this.loadSequenceId + 1;
        this.loadSequenceId = sequenceId;
        return sequenceId;
    }
}
